<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { jobsApi, type JobInfo, ApiError } from '../lib/api';
  import { formatRelative } from '../lib/time';
  import {
    connectJobsStream,
    type JobStartedEvent,
    type JobFinishedEvent,
    type JobScheduledEvent,
    type JobLogEvent,
  } from '../lib/stream';
  import JobCard from '../components/JobCard.svelte';
  import HistoryDrawer from '../components/HistoryDrawer.svelte';
  import Costs from './Costs.svelte';
  import Recipients from './Recipients.svelte';
  import Reports from './Reports.svelte';
  import { MonitorsView } from '../lib/monitors-view';
  import { navigation } from '../lib/navigation.svelte';

  // A tela ativa vem do CAMINHO — /jobs e /costs. Link direto abre a tela certa
  // e o voltar do navegador troca de tela em vez de sair do painel.
  const view = $derived(MonitorsView.fromPath(navigation.path));

  interface Props {
    username: string;
    onLogout: () => void;
  }

  let { username, onLogout }: Props = $props();

  let jobs = $state<JobInfo[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let lastFetchedAt = $state<Date | null>(null);
  let triggering = $state<Set<string>>(new Set());
  let drawerJobName = $state<string | null>(null);
  let streamConnected = $state(false);
  // A tela de Custos busca sozinha: enquanto ela está aberta, o selo do topo
  // conta o que ELA sabe, senão diria "ao vivo" sobre o stream dos jobs.
  let costsFetchedAt = $state<Date | null>(null);
  let costsLive = $state(false);

  // Quem está na tela responde pelo selo: em Custos, a própria tela; em Jobs, o
  // stream de eventos.
  const live = $derived(view.showsCosts ? costsLive : streamConnected);
  const fetchedAt = $derived(view.showsCosts ? costsFetchedAt : lastFetchedAt);

  // logs por job — limitado às últimas 30 linhas por job pra não estourar memória
  let logsByJob = $state<Map<string, JobLogEvent[]>>(new Map());

  const LOG_BUFFER = 30;

  const REFRESH_MS = 30_000;
  let intervalId: ReturnType<typeof setInterval> | null = null;
  let closeStream: (() => void) | null = null;

  function updateJob(name: string, patch: Partial<JobInfo>): void {
    jobs = jobs.map((j) => (j.name === name ? { ...j, ...patch } : j));
  }

  function onStarted(e: JobStartedEvent): void {
    updateJob(e.name, {
      lastRun: {
        startedAt: e.startedAt,
        finishedAt: null,
        status: 'running',
        durationMs: null,
        errorMessage: null,
        triggerSource: e.source,
      },
    });
  }

  function onFinished(e: JobFinishedEvent): void {
    updateJob(e.name, {
      lastRun: {
        startedAt: e.startedAt,
        finishedAt: e.finishedAt,
        status: e.status,
        durationMs: e.durationMs,
        errorMessage: e.errorMessage,
      },
    });
    // Garante que botão "Rodar agora" volta ao normal
    if (triggering.has(e.name)) {
      const next = new Set(triggering);
      next.delete(e.name);
      triggering = next;
    }
  }

  function onScheduled(e: JobScheduledEvent): void {
    updateJob(e.name, {
      schedule: e.schedule,
      scheduleDefault: e.scheduleDefault,
      scheduleIsOverridden: e.isOverride,
    });
  }

  function onLog(e: JobLogEvent): void {
    const next = new Map(logsByJob);
    const existing = next.get(e.name) ?? [];
    const updated = [...existing, e].slice(-LOG_BUFFER);
    next.set(e.name, updated);
    logsByJob = next;
  }

  function onStartedKeepLogs(e: JobStartedEvent): void {
    // Mantém logs do run anterior — separador visual pode vir depois se quisermos.
    // Os logs novos vão se concatenar na sequência via onLog (buffer de 30 linhas).
    onStarted(e);
  }

  async function load(): Promise<void> {
    try {
      const res = await jobsApi.list();
      jobs = res.jobs;
      lastFetchedAt = new Date();
      error = null;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onLogout();
        return;
      }
      error = err instanceof Error ? err.message : 'erro ao carregar';
    } finally {
      loading = false;
    }
  }

  // Hidrata logs do DB ao mount pra que refresh não perca histórico.
  // SSE depois adiciona novos em tempo real por cima.
  async function hydrateLogs(): Promise<void> {
    const next = new Map<string, JobLogEvent[]>();
    await Promise.all(
      jobs.map(async (job) => {
        try {
          const res = await jobsApi.logs(job.name, LOG_BUFFER);
          next.set(
            job.name,
            res.logs.map((l) => ({
              type: 'job.log' as const,
              name: job.name,
              level: l.level,
              message: l.message,
              data: l.data,
              timestamp: l.timestamp,
            })),
          );
        } catch {
          // job sem logs ainda ou DB indisponível — ignora
        }
      }),
    );
    logsByJob = next;
  }

  async function trigger(name: string): Promise<void> {
    const next = new Set(triggering);
    next.add(name);
    triggering = next;
    try {
      await jobsApi.trigger(name);
      // Refresh logo em seguida pra pegar status 'running'
      setTimeout(() => void load(), 500);
    } catch (err) {
      console.error('trigger failed', err);
    } finally {
      // Solta o estado triggering depois de 2s pra UI ter resposta visual
      setTimeout(() => {
        const next2 = new Set(triggering);
        next2.delete(name);
        triggering = next2;
      }, 2000);
    }
  }

  function handleVisibility(): void {
    if (document.visibilityState === 'visible' && !intervalId) {
      void load();
      intervalId = setInterval(() => void load(), REFRESH_MS);
    } else if (document.visibilityState === 'hidden' && intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  }

  onMount(() => {
    void (async () => {
      await load();
      // Após carregar a lista de jobs, hidrata logs persistidos do DB.
      // SSE depois empurra novos em tempo real por cima.
      await hydrateLogs();
    })();
    intervalId = setInterval(() => void load(), REFRESH_MS);
    document.addEventListener('visibilitychange', handleVisibility);

    // Conecta SSE pra updates em tempo real. Polling continua como fallback
    // — se SSE cai, browser reconecta sozinho; em paralelo o polling cobre
    // o intervalo em que o stream fica fora.
    closeStream = connectJobsStream({
      onJobStarted: onStartedKeepLogs,
      onJobFinished: onFinished,
      onJobScheduled: onScheduled,
      onJobLog: onLog,
      onOpen: () => (streamConnected = true),
      onError: () => (streamConnected = false),
    });
  });

  onDestroy(() => {
    if (intervalId) clearInterval(intervalId);
    document.removeEventListener('visibilitychange', handleVisibility);
    closeStream?.();
  });
</script>

<div class="layout">
  <header class="topbar">
    <div class="brand">
      <span class="brand-icon">⚡</span>
      <span class="brand-name">Desenrola Monitors</span>
    </div>
    <nav class="nav">
      {#each MonitorsView.all() as option (option.path)}
        <button
          class="nav-item"
          class:active={option.is(view)}
          onclick={() => navigation.goTo(option.path)}
        >
          {option.label}
        </button>
      {/each}
    </nav>
    <div class="topbar-right">
      <!-- Envios e relatórios não são ao vivo: o selo falaria do stream dos jobs. -->
      {#if !view.showsRecipients && !view.showsReports}
        <span
          class="stream-indicator"
          class:stream-live={live}
          title={live
            ? 'Recebendo dado novo em tempo real'
            : view.showsCosts
              ? 'Período fechado — o gasto não muda mais'
              : 'Sem conexão de stream — busca a cada 30s'}
        >
          <span class="stream-dot"></span>
          <span class="stream-label">{live ? 'ao vivo' : view.showsCosts ? 'fechado' : 'polling'}</span>
        </span>
        {#if fetchedAt}
          <span class="last-update mono">
            atualizado {formatRelative(fetchedAt.toISOString())}
          </span>
        {/if}
      {/if}
      <span class="user">{username}</span>
      <button class="logout-btn" onclick={onLogout}>Sair</button>
    </div>
  </header>

  <main class="content">
    {#if view.showsRecipients}
      <Recipients onUnauthorized={onLogout} />
    {:else if view.showsReports}
      <Reports onUnauthorized={onLogout} />
    {:else if view.showsCosts}
      <Costs
        onUnauthorized={onLogout}
        onRefreshed={(at, streaming) => {
          costsFetchedAt = at;
          costsLive = streaming;
        }}
      />
    {:else if loading}
      <p class="state">carregando jobs…</p>
    {:else if error}
      <p class="state state-error">{error}</p>
    {:else if jobs.length === 0}
      <p class="state">Nenhum job registrado.</p>
    {:else}
      <div class="grid">
        {#each jobs as job (job.name)}
          <JobCard
            {job}
            triggering={triggering.has(job.name)}
            logs={logsByJob.get(job.name) ?? []}
            onTrigger={() => trigger(job.name)}
            onViewHistory={() => (drawerJobName = job.name)}
            onScheduleChanged={() => void load()}
          />
        {/each}
      </div>
    {/if}
  </main>

  {#if drawerJobName}
    <HistoryDrawer jobName={drawerJobName} onClose={() => (drawerJobName = null)} />
  {/if}
</div>

<style>
  .layout {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: var(--bg-base);
  }

  .topbar {
    padding: var(--space-3) var(--space-6);
    background: var(--bg-elevated);
    border-bottom: 1px solid var(--border-subtle);
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .brand {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }
  .brand-icon {
    font-size: 18px;
  }
  .brand-name {
    font-weight: 600;
    letter-spacing: -0.01em;
  }

  .nav {
    display: flex;
    gap: var(--space-1);
    margin-left: var(--space-6);
    flex: 1;
  }
  .nav-item {
    padding: var(--space-2) var(--space-3);
    font-size: 13px;
    color: var(--text-tertiary);
    border-radius: var(--radius-sm);
    cursor: pointer;
    transition: all var(--duration-fast) var(--easing-default);
  }
  .nav-item:hover {
    color: var(--text-secondary);
    background: var(--bg-hover);
  }
  .nav-item.active {
    color: var(--text-primary);
    background: var(--bg-hover);
  }

  .topbar-right {
    display: flex;
    align-items: center;
    gap: var(--space-4);
  }
  .last-update {
    color: var(--text-tertiary);
    font-size: 12px;
  }
  .stream-indicator {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-tertiary);
  }
  .stream-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--text-disabled);
    transition: background var(--duration-normal) var(--easing-default);
  }
  .stream-indicator.stream-live .stream-dot {
    background: var(--color-success);
    box-shadow: 0 0 6px var(--color-success);
    animation: pulse-live 1.6s var(--easing-default) infinite;
  }
  .stream-indicator.stream-live {
    color: var(--color-success);
  }
  @keyframes pulse-live {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
  .user {
    color: var(--text-secondary);
    font-size: 13px;
  }
  .logout-btn {
    color: var(--text-secondary);
    font-size: 12px;
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    transition: all var(--duration-fast) var(--easing-default);
  }
  .logout-btn:hover {
    background: var(--bg-hover);
    color: var(--text-primary);
  }

  .content {
    flex: 1;
    max-width: 1280px;
    margin: 0 auto;
    width: 100%;
    padding: var(--space-6);
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
    gap: var(--space-4);
  }

  .state {
    color: var(--text-tertiary);
    text-align: center;
    margin: var(--space-7) 0;
  }
  .state-error {
    color: var(--color-danger);
  }
</style>
